import React from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
} from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2' // Grid version 2
import { Stack } from '@mui/material'
import Header from './Header'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import BomTable from './BomTable.js'
import BomGridTotal from './BomGridTotal.js'
import BomGridButtonRow from './BomGridButtonRow.js'
import GridTitleRow from './GridTitleRow.js'
import fenceCalc from '../utilities/fenceCalc'
import * as Money from '../utilities/money'
import {
    useStockOutages,
    isOutOfStock,
    marketFromIsCAD,
    marketLabel,
    outageNote,
} from '../data/stockOutages'

function getSummaryTypography(fenceSummaryRpt) {
    const typoAr = fenceSummaryRpt.split('\n')

    return typoAr.map((item, i) => {
        return <Typography key={i}>{item}</Typography>
    })
}

function getOrderTotal(bomLineItems) {
    const orderTotalNum = bomLineItems.reduce((acc, { itemQty, sugRetail }) => {
        return acc + itemQty * Money.strToIntPennies(sugRetail)
    }, 0)

    return orderTotalNum
}
function getStorageArObj({ bomLineItems }) {
    const storageArObj = bomLineItems.map((item) => {
        const { productIDNum, itemQty: quantity } = item
        return { productIDNum, quantity }
    })
    return storageArObj
}

export default function BOMGrid({
    sessionObj,
    invList,
    isCAD,
    setIsCADCurrency,
}) {
    const [bomState, setBomState] = React.useState({
        bomLineItems: [],
        fenceSummaryRpt: '',
    })
    const [openSummaryDlg, setOpenSummaryDlg] = React.useState(false)
    const theme = useTheme()
    const isDesktopDisplay = useMediaQuery(theme.breakpoints.up('md'))

    // ─── Stock outages ────────────────────────────────────────────────
    // The BOM keeps showing every item the fence needs — a customer must
    // know they need screws even if we can't ship them this week. Items
    // out of stock in the current market are simply not pushed to the
    // cart, and the notice below the table names them.
    const outages = useStockOutages()
    const market = marketFromIsCAD(isCAD)

    // Tracks quantities the user has manually changed
    // Key: productIDNum, Value: edited quantity
    // useRef so it persists across re-renders without triggering them
    const userEditsRef = React.useRef({})

    React.useEffect(() => {
        let { materialsList, fenceSummaryRpt } = fenceCalc(sessionObj, invList)
        let bomLineItems = materialsList.map(
            ({
                productIDNum,
                href,
                productID,
                descriptionTitle,
                pkgQty,
                itemQty,
                srp,
                displayOrder,
            }) => {
                const sugRetail = Money.getMoneyFormat(srp)

                // If the user manually edited this item's qty, keep their value
                const editedQty = userEditsRef.current[productIDNum]
                const finalQty = editedQty !== undefined ? editedQty : itemQty

                return {
                    productIDNum,
                    href,
                    productID,
                    descriptionTitle,
                    pkgQty,
                    sugRetail,
                    itemQty: finalQty,
                    displayOrder,
                }
            }
        )
        setBomState({ bomLineItems, fenceSummaryRpt })
    }, [sessionObj, invList])

    // Lines in the current BOM that are out of stock in this market and
    // have a non-zero quantity (zero-qty lines are irrelevant to the user).
    const blockedLines = bomState.bomLineItems.filter(
        ({ productIDNum, itemQty }) =>
            Number(itemQty) > 0 && isOutOfStock(outages, productIDNum, market)
    )

    const handleSaveToCart = () => {
        const storageArObj = getStorageArObj(bomState)
        const filteredStoreageArObj = storageArObj.filter(
            ({ productIDNum }) => {
                return (
                    // remove any non inventory items (placeholders)
                    productIDNum.slice(0, 3) !== '999' &&
                    // remove items out of stock in this market
                    !isOutOfStock(outages, productIDNum, market)
                )
            }
        )

        if (window.fbq) {
            window.fbq('track', 'AddToCart') // or 'Purchase', 'Contact', etc.
        }

        const filteredStoreageArObj2 = filteredStoreageArObj.filter(
            ({ quantity }) => quantity
        )

        localStorage.setItem(
            'shopping-cart',
            JSON.stringify(filteredStoreageArObj2)
        )
        window.location.href = '/shop/cart'
    }

    const orderTotal = Money.intPenniesToStr(
        getOrderTotal(bomState.bomLineItems)
    )

    const handleInputChange = (
        { target: { value: itemQty } },
        { productIDNum: productIDNumChg }
    ) => {
        // Record that the user intentionally changed this quantity
        userEditsRef.current[productIDNumChg] = itemQty

        setBomState((bomState) => {
            let bomLineItems = bomState.bomLineItems.map((lineItem) => {
                if (lineItem.productIDNum === productIDNumChg) {
                    return { ...lineItem, itemQty }
                }
                return lineItem
            })

            return { ...bomState, bomLineItems }
        })
    }

    const handleValue = (productIDNumChg) => {
        const lineItem = bomState.bomLineItems.find(
            ({ productIDNum }) => productIDNum === productIDNumChg
        )
        return lineItem.itemQty || 0
    }

    function handleClose() {
        setOpenSummaryDlg(false)
    }

    let isLoaded =
        Array.isArray(bomState.bomLineItems) && bomState.bomLineItems.length

    return !isLoaded ? (
        <Stack
            direction={'row'}
            alignItems="center"
            justifyContent={'space-evenly'}
        >
            <CircularProgress sx={{ my: 10 }} />
            <Typography sx={{ my: 10, fontWeight: 500, fontSize: '1.3rem' }}>
                Loading Fencing Estimate
            </Typography>
            <CircularProgress sx={{ my: 10 }} />
            <Typography sx={{ my: 10, fontWeight: 500, fontSize: '1.3rem' }}>
                Loading Fencing Estimate
            </Typography>
            <CircularProgress sx={{ my: 10 }} />
        </Stack>
    ) : (
        <>
            <Grid2
                container
                columns={20}
                xs={20}
                sx={{
                    border: 2,
                    borderColor: 'primary.main',
                }}
            >
                <Box
                    sx={{
                        minWidth: '100%',
                    }}
                >
                    <GridTitleRow />

                    {isDesktopDisplay && <Header />}
                    <BomTable
                        bomLineItems={bomState.bomLineItems}
                        handleInputChange={handleInputChange}
                        handleValue={handleValue}
                        isDesktopDisplay={isDesktopDisplay}
                        invList={invList}
                    />

                    {/* Out-of-stock notice: only when this estimate
                        includes an item we can't currently ship to
                        this market. Lists each affected line. */}
                    {blockedLines.length > 0 && (
                        <Box
                            role="status"
                            sx={{
                                m: 2,
                                p: 2,
                                border: `1px solid rgba(185, 134, 71, 0.4)`,
                                backgroundColor: 'rgba(185, 134, 71, 0.08)',
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                                Our Apologies. We are temporarily out of stock
                                in these items in {marketLabel(market)}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {blockedLines.map((line) => (
                                    <Typography
                                        key={line.productIDNum}
                                        component="li"
                                        variant="body2"
                                    >
                                        {line.descriptionTitle} (
                                        {line.productID})
                                        {outageNote(outages, line.productIDNum)
                                            ? ` — ${outageNote(
                                                  outages,
                                                  line.productIDNum
                                              )}`
                                            : ''}
                                    </Typography>
                                ))}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Your estimate still shows the quantities you
                                will need,{' '}
                                {blockedLines.length > 1
                                    ? 'but these items'
                                    : 'but this item'}{' '}
                                can not be added to your cart.
                            </Typography>
                        </Box>
                    )}

                    <BomGridTotal
                        isDesktopDisplay={isDesktopDisplay}
                        orderTotal={orderTotal}
                        bomLineItems={bomState.bomLineItems}
                        isCAD={isCAD}
                        setIsCADCurrency={setIsCADCurrency}
                    />
                    <BomGridButtonRow
                        setOpenSummaryDlg={setOpenSummaryDlg}
                        handleSaveToCart={handleSaveToCart}
                    />
                </Box>
            </Grid2>

            <Dialog open={openSummaryDlg}>
                <DialogTitle>Fence Summary</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'black' }}>
                        {getSummaryTypography(bomState.fenceSummaryRpt)}
                    </DialogContentText>
                    <DialogActions sx={{ marginTop: '1rem' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleClose}
                            autoFocus
                        >
                            Close
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    )
}
